import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import data from '../data/paomakeup';
import { useFetch } from '../hooks/useApiCall';
import { getCategories } from '../services/serviceService';
import { mapCategoriesToPriceData } from '../utils/dataMappers';
import { PmLoading, PmError, PmEmpty } from '../components/pm/PmStates';
import { IconArrow, IconCheck } from '../components/pm/PmIcons';
import { useCart } from '../context/CartContext';

const resolveImageSrc = (src) => {
  if (!src) return '';
  if (/^https?:\/\//.test(src) || src.startsWith('/')) return src;
  return `../../../${src}`;
};

const OurServicesSlug = () => {
  const { slug: pageIdSlug } = useParams();
  const navigate = useNavigate();
  const { data: apiCategories, loading, error, refetch } = useFetch(getCategories);
  const { addService, removeService, isServiceInCart, cartServices } = useCart();

  const priceData = apiCategories
    ? mapCategoriesToPriceData(apiCategories)
    : data.our_services_price;

  const filteredServices = priceData.find((service) => service.service_slug === pageIdSlug);

  const handleToggleService = (service, subServiceName) => {
    if (!service.id) return;
    if (isServiceInCart(service.id)) {
      removeService(service.id);
    } else {
      addService({
        label: service.name,
        shortName: service.name,
        categoryName: filteredServices.service_name,
        subCategoryName: subServiceName || '',
        serviceId: service.id,
        price: service.price,
      });
    }
  };

  let body;
  if (loading) {
    body = <PmLoading message="Cargando los precios…" />;
  } else if (!filteredServices && error) {
    body = <PmError message={error} onRetry={refetch} />;
  } else if (!filteredServices) {
    body = <PmEmpty title="Servicio no encontrado" text="Es posible que el enlace no sea válido o la categoría ya no exista." />;
  } else {
    body = (
      <div className="cat-detail">
        <div className="cat-detail__media">
          <img
            src={resolveImageSrc(filteredServices.service_image)}
            alt={filteredServices.service_name}
            loading="eager"
            decoding="async"
          />
        </div>

        <div className="price-card">
          <div className="price-card__head">
            <span>{filteredServices.service_name}</span>
          </div>
          <div className="price-card__body">
            {(filteredServices.sub_services || []).map((subService, index) => (
              <div key={index}>
                {subService.sub_service_name && (
                  <p className="price-group__name">{subService.sub_service_name}</p>
                )}
                {subService.description && (
                  <p className="price-group__desc">{subService.description}</p>
                )}
                {subService.services.map((service, subIndex) => {
                  const inCart = service.id && isServiceInCart(service.id);
                  return (
                    <div className="price-row" key={subIndex}>
                      <span>{service.name}</span>
                      <span className="price-row__end">
                        <span className="price-row__price">{service.price}</span>
                        {service.id && (
                          <button
                            type="button"
                            onClick={() => handleToggleService(service, subService.sub_service_name)}
                            className={`add-btn${inCart ? ' is-added' : ''}`}
                          >
                            {inCart ? <><IconCheck strokeWidth="3" /> Agregado</> : 'Agregar'}
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
            {(filteredServices.services || []).map((service, idx) => (
              <div className="price-row" key={`flat-${idx}`}>
                <span>{service.name}</span>
                <span className="price-row__price">{service.price}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="pm-scope page"
      style={{ paddingBottom: cartServices.length > 0 ? 70 : undefined }}
    >
      <Helmet>
        <title>{filteredServices ? `${filteredServices.service_name} - ${data.title}` : `Precios - ${data.title}`}</title>
        <meta name="description" content={data.our_services_price_description} />
        <meta name="keywords" content={data.our_services_price_keywords} />
        <meta property="og:title" content={`Precios - ${data.title}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`/nuestros-servicios/${pageIdSlug}/precios`} />
        <meta property="og:site_name" content="PaoMakeup Beauty Studio" />
        <meta property="og:description" content={data.our_services_price_description} />
      </Helmet>

      <section className="section">
        <div className="container">
          <button type="button" className="back-link" onClick={() => navigate(-1)} style={{ border: 0, background: 'transparent', cursor: 'pointer' }}>
            <IconArrow style={{ transform: 'rotate(180deg)' }} /> Regresar
          </button>

          <div className="section-head">
            <span className="eyebrow">Servicios y precios</span>
            <h1 className="text-balance">
              {filteredServices ? filteredServices.service_name : 'Conoce nuestros servicios y precios'}
            </h1>
            <p>Agrega los servicios que deseas y haz clic en Continuar para agendar tu cita.</p>
          </div>

          {body}
        </div>
      </section>
    </div>
  );
};

export default OurServicesSlug;
